import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MailModal({ open, onClose }) {
    const { t, lang } = useLanguage();
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [loading, setLoading] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, { ...form, language: lang });
            toast.success(t.mail.success);
            setForm({ name: "", email: "", message: "" });
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(t.mail.error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6"
                    data-testid="mail-modal"
                >
                    <div
                        className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
                        onClick={onClose}
                        data-testid="mail-modal-backdrop"
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 30, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.97 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="relative w-full max-w-lg bg-cream rounded-3xl border border-gold/25 p-6 sm:p-10 shadow-[0_40px_90px_-20px_rgba(0,0,0,0.4)] max-h-[90vh] overflow-y-auto"
                    >
                        <button
                            onClick={onClose}
                            className="absolute top-5 right-5 w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-ink hover:border-gold hover:text-gold transition-colors"
                            data-testid="mail-modal-close"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        <div className="eyebrow mb-4">Frizerka</div>
                        <h3 className="font-display text-ink text-3xl sm:text-4xl tracking-tight leading-[1.05] mb-4">
                            {t.mail.title.split(" ")[0]}{" "}
                            <span className="serif-italic text-gold">
                                {t.mail.title.split(" ").slice(1).join(" ")}
                            </span>
                        </h3>
                        <p className="text-ink/70 font-sans text-sm leading-relaxed mb-8 max-w-sm">
                            {t.mail.sub}
                        </p>

                        <form onSubmit={onSubmit} className="flex flex-col gap-5" data-testid="mail-modal-form">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label className="block eyebrow mb-3">{t.mail.name}</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        required
                                        data-testid="mail-modal-input-name"
                                        className="w-full bg-white border border-gold/25 rounded-full px-5 py-3.5 text-ink placeholder-ink/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/50 transition-colors font-sans"
                                    />
                                </div>
                                <div>
                                    <label className="block eyebrow mb-3">{t.mail.email}</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={onChange}
                                        required
                                        data-testid="mail-modal-input-email"
                                        className="w-full bg-white border border-gold/25 rounded-full px-5 py-3.5 text-ink placeholder-ink/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/50 transition-colors font-sans"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block eyebrow mb-3">{t.mail.message}</label>
                                <textarea
                                    name="message"
                                    rows={5}
                                    value={form.message}
                                    onChange={onChange}
                                    required
                                    placeholder={t.mail.placeholder}
                                    data-testid="mail-modal-input-message"
                                    className="w-full bg-white border border-gold/25 rounded-2xl px-5 py-4 text-ink placeholder-ink/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/50 transition-colors font-sans resize-none"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-gold inline-flex items-center justify-center gap-2 disabled:opacity-60 w-full"
                                data-testid="mail-modal-submit"
                            >
                                <Send className="w-4 h-4" />
                                {loading ? t.mail.sending : t.mail.submit}
                            </button>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
